import { Pagination, Tooltip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowCircleRightIcon } from "../../assets/icons/arrow-circle-right.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as FolderPlusIcon } from "../../assets/icons/folder-plus.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/more.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import BackButton from '../../components/common/BackButton';
import PageSizeSelector from '../../components/common/PageSizeSelector';
import ResponsiveTable, { ResponsiveTableColumn } from "../../components/common/ResponsiveTable";
import SearchWithTagsInput from '../../components/common/SearchWithTagsInput';
import {
    ControlsRow,
    PageSizeWrapper,
    PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../components/common/TableContainer.styled";
import CreateDataSourceDialog from "../../components/data-sources/CreateDataSourceDialog";
import { ActionButton, ContextMenu, ContextMenuItem, OptionsButton } from "../../components/data-sources/DataSourceTable.styled";
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';
import GenericInputDialog from "../../components/dialogs/InputDialog";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { DataSource } from "../../models/models";
import { collectionService } from "../../services/collection.service";
import { parseError } from "../../services/http.service";
import { transformDataSources } from "../../utils/format-data-sources";
import { Header } from './UserDataSourceList.styled';

const UserDataSourceList: React.FC = () => {
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [searchTags, setSearchTags] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalElements, setTotalElements] = useState(0);
    const pageSizeOptions = [5, 10, 20, 50];
    const [dataSourceToDelete, setDataSourceToDelete] = useState<DataSource | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [dataSourceToChangeName, setDataSourceToChangeName] = useState<DataSource | null>(null);
    const [isChangeNameDialogOpen, setIsChangeNameDialogOpen] = useState(false);
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const fetchDataSources = useCallback(async () => {
        setLoading(true);
        try {
            if (searchTags.length > 0) {
                const params = new URLSearchParams();
                params.append('page', (currentPage - 1).toString());
                params.append('size', pageSize.toString());
                searchTags.forEach(tag => params.append('name', tag));

                const response = await axios.get('/documents/by-collection', {params});
                setDataSources(transformDataSources(response.data.content));
                setTotalElements(response.data.totalElements || 0);
            } else {
                collectionService.invalidateCache();
                const data = await collectionService.fetchCollections(currentPage - 1, pageSize);
                setDataSources(transformDataSources(data.content));
                setTotalElements(data.totalElements);
            }
        } catch (error) {
            setDataSources([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTags]);

    useEffect(() => {
        void fetchDataSources();
    }, [fetchDataSources]);

    const handleTagsChange = (tags: string[]) => {
        setSearchTags(tags);
        setCurrentPage(1);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const openDeleteDialog = (dataSourceId: number) => {

        const dataSourceToSet = dataSources.find(c => c.id === dataSourceId);
        if (dataSourceToSet) {
            setDataSourceToDelete(dataSourceToSet);
            setIsDeleteDialogOpen(true);
        }
    };

    const openChangeNameDialog = (dataSourceId: number) => {
        const dataSourceToSet = dataSources.find(c => c.id === dataSourceId);
        if (dataSourceToSet) {
            setDataSourceToChangeName(dataSourceToSet);
            setIsChangeNameDialogOpen(true);
        }
    };

    const confirmDeleteDataSource = async () => {
        if (!dataSourceToDelete) return;
        try {
            await axios.delete(`/collections/${dataSourceToDelete.id}/documents`);
            collectionService.invalidateCache();
            if (currentPage > 1 && dataSources.length === 1) {
                setCurrentPage(currentPage - 1);
            } else {
                await fetchDataSources();
            }
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsDeleteDialogOpen(false);
            setDataSourceToDelete(null);
        }
    };

    const confirmChangeNameDataSource = async (dataSourceName: string) => {
        if (!dataSourceToChangeName) return;
        try {
            await axios.patch(`/collections/${dataSourceToChangeName.id}/name`, null, {
                params: {
                    collectionName: dataSourceName
                }
            });
            await fetchDataSources();
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsChangeNameDialogOpen(false);
            setDataSourceToChangeName(null);
        }
    };

    const handleDataSourcePreview = useCallback((dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`)
    }, [navigate]);

    const handleDataSourceCreated = (dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`);
    };

    const translateCollectionType = React.useCallback((type: string): string => {
        switch (type) {
            case 'MANUAL':
                return t('dataSourceTable.collectionType.manual');
            case 'AUTOMATIC':
                return t('dataSourceTable.collectionType.automatic');
            case 'DEFAULT':
                return t('dataSourceTable.collectionType.default');
            default:
                return type;
        }
    }, [t]);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        dataSource: DataSource;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, dataSource: DataSource) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            dataSource: dataSource,
        });
    }, []);

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handlePreviewClick = React.useCallback((dataSourceId: number, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        handleDataSourcePreview(dataSourceId);
    }, [handleDataSourcePreview]);

    const columns: ResponsiveTableColumn<DataSource>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceTable.headers.name'),
            render: (ds) => (
                <button
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handlePreviewClick(ds.id, e)}
                    style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, margin: 0, color: 'inherit', textAlign: 'left' }}
                >
                    {ds.name}
                </button>
            ),
        },
        {
            id: 'lastModification',
            header: t('dataSourceTable.headers.lastModification'),
            render: (ds) => new Date(ds.lastModificationDate).toLocaleDateString(),
        },
        {
            id: 'documents',
            header: t('dataSourceTable.headers.documents'),
            render: (ds) => ds.amountOfDocuments,
        },
        {
            id: 'type',
            header: t('dataSourceTable.headers.type'),
            render: (ds) => translateCollectionType(String(ds.collectionType)),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (ds) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            {t('adminDataSourceList.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            size="small"
                            style={{ width: 26 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            <MoreIcon/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, handlePreviewClick, translateCollectionType, handleContextMenu]);

    if (loading && dataSources.length === 0) return <div>Loading...</div>;

    return (
        <>
            <StyledTableContainer>
                <BackButton/>
                <Header>
                    <h2>{t('userDataSourceList.title')}</h2>
                    <ActionButton
                        variant="contained"
                        onClick={() => setIsCreateDialogOpen(true)}
                        startIcon={<FolderPlusIcon/>}
                        sx={{backgroundColor: 'primary.main'}}
                    >
                        {t('userDataSourceList.createButton')}
                    </ActionButton>
                </Header>

                <ControlsRow>
                    <SearchWrapper>
                        <SearchWithTagsInput
                            onTagsChange={handleTagsChange}
                            initialTags={searchTags}
                        />
                    </SearchWrapper>
                    <PageSizeWrapper>
                        <PageSizeSelector
                            options={pageSizeOptions}
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                        />
                    </PageSizeWrapper>
                </ControlsRow>

                <ResponsiveTable
                    columns={columns}
                    data={dataSources}
                    getRowKey={(ds) => ds.id}
                    emptyState={t('userDataSourceList.emptyState')}
                />

                <ContextMenu
                    open={contextMenu !== null}
                    onClose={handleCloseContextMenu}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    {contextMenu && [
                        (
                            <ContextMenuItem key="details" onClick={() => {
                                handleCloseContextMenu();
                                handleDataSourcePreview(contextMenu.dataSource.id);
                            }}>
                                <ArrowCircleRightIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                {t('dataSourceTable.contextMenu.details')}
                            </ContextMenuItem>
                        ),
                        (
                            <ContextMenuItem key="changeName" onClick={() => {
                                handleCloseContextMenu();
                                openChangeNameDialog(contextMenu.dataSource.id);
                            }}>
                                <EditIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                {t('dataSourceTable.contextMenu.changeName')}
                            </ContextMenuItem>
                        ),
                        (
                            <ContextMenuItem key="delete"
                                onClick={() => {
                                    handleCloseContextMenu();
                                    openDeleteDialog(contextMenu.dataSource.id);
                                }}
                                style={{color: '#f44336'}}
                            >
                                <TrashIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                {t('dataSourceTable.contextMenu.delete')}
                            </ContextMenuItem>
                        ),
                    ]}
                </ContextMenu>

                {totalElements > 5 && (
                    <PaginationWrapper>
                        <Pagination
                            count={Math.ceil(totalElements / pageSize)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </PaginationWrapper>
                )}
            </StyledTableContainer>

            <CreateDataSourceDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onDataSourceCreated={handleDataSourceCreated}
            />

            {dataSourceToDelete && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={confirmDeleteDataSource}
                    title={t('userDataSourceList.deleteDialog.title')}
                    message={<>{t('userDataSourceList.deleteDialog.message')} &ldquo;{dataSourceToDelete.name}&rdquo;?</>}
                    confirmButtonText={t('userDataSourceList.deleteDialog.deleteButton') as string}
                    cancelButtonText={t('userDataSourceList.deleteDialog.cancelButton') as string}
                />
            )}

            {dataSourceToChangeName && (
                <GenericInputDialog
                    open={isChangeNameDialogOpen}
                    title={t('inputDialog.title')}
                    label={t('inputDialog.label')}
                    required
                    initialValue={dataSourceToChangeName.name}
                    onClose={() => setIsChangeNameDialogOpen(false)}
                    onConfirm={(val) => confirmChangeNameDataSource(val)}
                />
            )}
        </>
    );
};

export default UserDataSourceList;
